import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'

/**
 * Registers vue components globally from @/pages and @/components
 *
 * Components
 * Every top level Vue file inside @components is registered and everything inside top level folders starting with '_'
 * eg. @components/ProfileAvatar.vue => <ProfileAvatar />
 * eg. @components/_User/Account/PasswordChange.vue => <UserAccountPasswordChange />
 *
 * Pages
 * Every Vue file inside @pages is registered
 * eg. @pages/coaches/index.vue => <CoachesIndex /> || <coaches-index />
 * eg. @pages/coaches/patients/index.vue => <CoachesPatientsIndex /> || <coaches-patients-index />
 */
export default class ComponentAutoloader {
    constructor() {
        const requireComponents = require.context('@components', true, /[A-Z]\w+\.vue$/)
        const requirePages = require.context(
            '@pages',
            true,
            /[a-z]\w+\.vue$/
        )

        this.registerComponents(requireComponents)
        this.registerPages(requirePages)
    }

    static register() {
        return new this()
    }

    registerComponents(requireComponents) {
        requireComponents.keys().forEach((fileName) => {
            // Get component config
            const componentConfig = requireComponents(fileName)

            // Remove the "./_" from the beginning
            const pathFragments = fileName
                .replace(/^\.\//, '')
                .replace(/^\.\/_/, '')
                .split('/')

            // when folder
            if (pathFragments.length > 1) {
                // register component only when first folder name starts with '_'
                if (!_.first(pathFragments).startsWith('_')) {
                    return
                }
            }

            let componentName = ''

            for (let fragment of pathFragments) {
                // limit repetition eg. ./Coaches/Patients/PatientsTable.vue => <CoachesPatientsTable />
                if (fragment.startsWith(componentName)) {
                    fragment = fragment.replace(componentName, '')
                }

                // Get PascalCase name of component
                componentName += upperFirst(
                    camelCase(
                        fragment
                        // Remove the file extension from the end
                            .replace(/\.\w+$/, '')
                    )
                )
            }

            // Register component globally
            this.addComponent(componentName, componentConfig)
        })
    }

    registerPages(requirePages) {
        requirePages.keys().forEach((pathToFile) => {
            // Get component config
            const componentConfig = requirePages(pathToFile)

            // Get path fragments
            const pathFragments = pathToFile.split('/')

            // Remove the "." from the beginning
            pathFragments.shift()

            // Get PascalCase name of component
            const componentName = upperFirst(
                camelCase(
                    pathFragments
                        .join('-')
                    // Remove the file extension from the end
                        .replace(/\.\w+$/, '')
                )
            )

            // Register component globally
            this.addComponent(componentName, componentConfig)
        })
    }

    addComponent(componentName, componentConfig) {
        Vue.component(
            componentName,
            // Look for the component options on `.default`, which will
            // exist if the component was exported with `export default`,
            // otherwise fall back to module's root.
            componentConfig.default || componentConfig
        )
    }
}
